import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ArrowsClockwise, PaperPlaneTilt } from "@phosphor-icons/react";
import {
  KpiGrid, DailyActivityChart, EventTypeChart, TopVendors, RecentEvents,
} from "@/components/analytics/AnalyticsParts";

const PERIODS = [
  { d: 7, label: "7d" },
  { d: 30, label: "30d" },
  { d: 90, label: "90d" },
  { d: 180, label: "180d" },
  { d: 365, label: "365d" },
];

const fmtDay = (d) => {
  const dt = new Date(d + "T00:00:00Z");
  return dt.toLocaleDateString("en-IE", { day: "2-digit", month: "short", timeZone: "UTC" });
};

const prettyType = (t) => t.replace(/_/g, " ").replace(/\./g, " · ");

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    const [s, r] = await Promise.all([
      api.get(`/analytics/summary?days=${days}`),
      api.get(`/analytics/recent?limit=25`),
    ]);
    setData(s.data);
    setRecent(r.data);
    setLoading(false);
    setRefreshing(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const sendDigest = async () => {
    setSending(true); setSendResult(null);
    try {
      const r = await api.post("/analytics/digest/send", {});
      setSendResult(r.data);
    } catch (e) {
      setSendResult({ sent: 0, failed: 1, error: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
      setTimeout(() => setSendResult(null), 6000);
    }
  };

  const daily = useMemo(
    () => (data?.daily || []).map((p) => ({ ...p, label: fmtDay(p.day) })),
    [data]
  );
  const byType = useMemo(
    () => (data?.by_type || []).slice(0, 8).map((p) => ({ ...p, label: prettyType(p.event_type) })),
    [data]
  );
  const totalEvents = data?.total_events ?? 0;

  return (
    <div className="space-y-10" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Product Telemetry</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Analytics</h1>
          <p className="text-white/60 mt-2 text-sm max-w-2xl">
            Real-time engagement across every compliance surface — receipts, certificates, exports and emails.
            Only your own activity is shown. GDPR-safe: no PII is stored in telemetry events.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="glass rounded-full p-1 flex gap-1" data-testid="analytics-period-switch">
            {PERIODS.map((p) => (
              <button
                key={p.d}
                onClick={() => setDays(p.d)}
                data-testid={`period-${p.d}`}
                className={`px-3 py-1.5 rounded-full text-xs transition ${
                  days === p.d ? "bg-[#FFB020] text-[#050B0A] font-semibold" : "text-white/60 hover:text-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={load} disabled={refreshing} className="btn-ghost text-xs" data-testid="btn-refresh-analytics">
            <ArrowsClockwise size={13} weight="bold" className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={sendDigest}
            disabled={sending}
            className="btn-gold text-xs"
            data-testid="btn-send-digest"
            title="Send the weekly compliance digest by email now"
          >
            <PaperPlaneTilt size={13} weight="fill" />
            {sending ? "Sending…" : "Email digest"}
          </button>
        </div>
      </div>
      {sendResult && (
        <div
          className={`rounded-xl p-3 text-xs border ${sendResult.failed > 0 ? "bg-[#E63946]/10 border-[#E63946]/40 text-[#E63946]" : "bg-[#00A86B]/10 border-[#00A86B]/40 text-[#00A86B]"}`}
          data-testid="digest-send-result"
        >
          {sendResult.error
            ? `Digest failed: ${sendResult.error}`
            : `Digest emailed to ${sendResult.sent} recipient${sendResult.sent === 1 ? "" : "s"}${sendResult.failed ? ` · ${sendResult.failed} failed` : ""}.`}
        </div>
      )}

      {loading ? (
        <div className="glass rounded-xl p-16 text-center text-white/60 text-sm">Loading telemetry…</div>
      ) : (
        <>
          <KpiGrid kpis={data?.kpis} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <DailyActivityChart daily={daily} days={days} totalEvents={totalEvents} />
            <EventTypeChart byType={byType} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <TopVendors vendors={data?.top_vendors} />
            <RecentEvents recent={recent} />
          </div>
        </>
      )}
    </div>
  );
}
